// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * G12n Tariff Calculator (PGE-specific Night Tariff)
 *
 * @description Calculates costs for G12n night tariff (PGE-specific)
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type { G12nPricing, MonthlyResult, ConsumptionPattern } from './types';
import {
  VAT_RATE,
  HANDLING_FEE,
  SUBSCRIPTION_FEE,
  TRANSITION_FEE,
  POWER_FEE,
  OZE_FEE,
  COGENERATION_FEE,
  isPowerFeeFrozen,
} from './constants';
import { roundToTwoDecimals, distributeConsumption } from './helpers';

/**
 * Calculate G12n (PGE-specific Night Tariff) cost
 *
 * Formula (PGE-specific time windows):
 * - Night hours: 01:00-05:00 weekdays+Saturdays + all day Sundays/holidays (cheap night rate)
 * - Day hours: all other hours (expensive day rate)
 * - Energy cost = dayKwh × energyDayPrice + nightKwh × energyNightPrice + qualityCharge × totalKwh
 * - Distribution cost = fixedFee + (dayKwh × distDay) + (nightKwh × distNight)
 * - Total (gross) = subtotal × 1.23
 *
 * @param monthlyConsumption - kWh consumed in month
 * @param pricing - G12n pricing data
 * @param consumptionPattern - Hourly pattern (weekday/weekend)
 * @param daysInMonth - Number of days in month
 * @param year - Year (for holiday detection)
 * @param month - Month (0-indexed, 0=Jan)
 * @returns Monthly calculation result with day/night split
 */
export function calculateG12n(
  monthlyConsumption: number,
  pricing: G12nPricing,
  consumptionPattern: ConsumptionPattern,
  daysInMonth: number,
  year: number,
  month: number
): MonthlyResult {
  // Input validation - HIGH-1 Security: Validate for NaN, Infinity, and negative values
  if (!Number.isFinite(monthlyConsumption) || monthlyConsumption < 0) {
    throw new Error('Monthly consumption must be a finite non-negative number');
  }
  if (!pricing) {
    throw new Error('Pricing data is required');
  }
  if (!consumptionPattern) {
    throw new Error('Consumption pattern is required');
  }

  // Distribute consumption across hours
  const hourlyConsumption = distributeConsumption(
    monthlyConsumption,
    consumptionPattern,
    daysInMonth,
    year,
    month
  );

  // PGE G12n: Night is 01:00-05:00 on weekdays+Saturdays, all day on Sundays/holidays
  let dayKwh = 0;
  let nightKwh = 0;

  for (const hourData of hourlyConsumption) {
    // PGE G12n: Only Sundays (dayOfWeek=0) and holidays get all-day night rate
    // Saturdays follow weekday schedule (only 01:00-05:00 is night)
    const isSunday = hourData.dayOfWeek === 0;
    const isAllDayNight = isSunday || hourData.isHoliday;

    if (isAllDayNight) {
      // Sunday and holiday hours are all night (cheap)
      nightKwh += hourData.consumption;
    } else {
      // Weekday + Saturday: night is 01:00-05:00 (hours 1, 2, 3, 4)
      if (hourData.hour >= 1 && hourData.hour < 5) {
        nightKwh += hourData.consumption;
      } else {
        dayKwh += hourData.consumption;
      }
    }
  }

  // Calculate energy production cost (different rates for day/night)
  const qualityCharge = pricing.qualityCharge || 0;
  const energyDayCost = dayKwh * pricing.energyDayPricePerKwh;
  const energyNightCost = nightKwh * pricing.energyNightPricePerKwh;
  const qualityCost = monthlyConsumption * qualityCharge;
  const productionCost = energyDayCost + energyNightCost + qualityCost;

  // Calculate distribution cost
  const distributionDayCost = dayKwh * pricing.distributionDayPerKwh;
  const distributionNightCost = nightKwh * pricing.distributionNightPerKwh;
  const distributionCost = pricing.fixedMonthlyFee + distributionDayCost + distributionNightCost;

  // Get fees - use defaults if not provided
  const handlingFee = pricing.handlingFee ?? HANDLING_FEE.REGULATED;
  const subscriptionFee = pricing.subscriptionFee ?? SUBSCRIPTION_FEE;
  const transitionFee = pricing.transitionFee ?? TRANSITION_FEE.TIER_3;

  // Power fee is 0 for Jan-June 2025
  const basePowerFee = pricing.powerFee ?? POWER_FEE.TIER_3;
  const powerFee = isPowerFeeFrozen(month, year) ? 0 : basePowerFee;

  // Variable fees (per kWh)
  const ozeFeeAmount = monthlyConsumption * (pricing.ozeFee ?? OZE_FEE);
  const cogenerationFeeAmount = monthlyConsumption * (pricing.cogenerationFee ?? COGENERATION_FEE);

  // Calculate totals
  const subtotal = productionCost + distributionCost +
    handlingFee + subscriptionFee + transitionFee + powerFee +
    ozeFeeAmount + cogenerationFeeAmount;
  const totalCost = subtotal * (1 + VAT_RATE);

  return {
    totalKwh: roundToTwoDecimals(monthlyConsumption),
    dayKwh: roundToTwoDecimals(dayKwh),
    nightKwh: roundToTwoDecimals(nightKwh),
    productionCost: roundToTwoDecimals(productionCost),
    distributionCost: roundToTwoDecimals(distributionCost),
    handlingFee: roundToTwoDecimals(handlingFee),
    subscriptionFee: roundToTwoDecimals(subscriptionFee),
    transitionFee: roundToTwoDecimals(transitionFee),
    powerFee: roundToTwoDecimals(powerFee),
    ozeFee: roundToTwoDecimals(ozeFeeAmount),
    cogenerationFee: roundToTwoDecimals(cogenerationFeeAmount),
    subtotal: roundToTwoDecimals(subtotal),
    totalCost: roundToTwoDecimals(totalCost)
  };
}
